import React from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  Badge,
  Body1Strong,
  Caption1,
  ProgressBar,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { ApolloError } from '@apollo/client';
import Card from '../../common/card/Card';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { IFinanceIncompleteProgress } from '../types';

const useStyles = makeStyles({
  summary: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  secondaryText: {
    color: tokens.colorNeutralForeground2,
  },
  completed: {
    color: tokens.colorStatusSuccessForeground1,
  },
  categoryList: {
    listStyleType: 'none',
    margin: `${tokens.spacingVerticalL} 0 0`,
    padding: '0 0 15px',
  },
  categoryLabel: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
});

interface IFinanceIncompleteTaskProgressProps {
  progress?: IFinanceIncompleteProgress;
  loading: boolean;
  error?: ApolloError;
}

interface ITaskProgressRow {
  key: string;
  label: string;
  completed: number;
  total: number;
  percentComplete: number | null;
}

const formatTaskProgressHint = (row: ITaskProgressRow): string => {
  if (row.total === 0 || row.percentComplete === null) {
    return 'No tasks';
  }

  return `${row.percentComplete}% Complete · ${row.completed} out of ${row.total}`;
};

const SAPDeactivationProgress = ({
  progress,
  loading,
  error,
}: IFinanceIncompleteTaskProgressProps): JSX.Element => {
  const classes = useStyles();

  const actionItemRows: ITaskProgressRow[] = (progress?.actionItemsProgress ?? []).map((item) => ({
    key: String(item.actionItemId),
    label: item.description,
    completed: item.completedTaskCount,
    total: item.totalTaskCount,
    percentComplete: item.percentComplete ?? null,
  }));

  const completedTaskCount = progress?.completedTaskCount ?? 0;
  const totalTaskCount = progress?.totalTaskCount ?? 0;
  const overallPercentComplete = progress?.percentComplete ?? null;

  const overallProgressRow: ITaskProgressRow = {
    key: 'overall',
    label: 'Overall Progress',
    completed: completedTaskCount,
    total: totalTaskCount,
    percentComplete: overallPercentComplete,
  };
  const allTasksComplete = totalTaskCount > 0 && completedTaskCount === totalTaskCount;

  const renderProgressBar = (row: ITaskProgressRow): JSX.Element => (
    <ProgressBar
      aria-label={row.label}
      aria-valuetext={formatTaskProgressHint(row)}
      value={row.completed}
      max={row.total > 0 ? row.total : 1}
      thickness="large"
      shape="rounded"
      color={row.total > 0 && row.completed === row.total ? 'success' : 'brand'}
    />
  );

  const renderTaskProgress = (): JSX.Element => {
    if (error) {
      return (
        <LoadingErrorMessage loading={loading} error={error} actionName="loading task progress" />
      );
    }

    return (
      <div role="group" aria-label="Task progress">
        <Stack className={classes.summary} tokens={{ childrenGap: 12, padding: 12 }}>
          <Stack
            horizontal
            verticalAlign="center"
            horizontalAlign="space-between"
            tokens={{ childrenGap: 12 }}
          >
            <Stack tokens={{ childrenGap: 2 }}>
              <Body1Strong>Overall Progress</Body1Strong>
              <Caption1 className={classes.secondaryText}>
                {totalTaskCount > 0
                  ? `${completedTaskCount} of ${totalTaskCount} tasks complete`
                  : 'No tasks'}
              </Caption1>
            </Stack>
            {overallPercentComplete !== null && (
              <StackItem disableShrink>
                <Title3 className={allTasksComplete ? classes.completed : undefined}>
                  {overallPercentComplete}%
                </Title3>
              </StackItem>
            )}
          </Stack>
          {renderProgressBar(overallProgressRow)}
        </Stack>
        <Stack as="ul" className={classes.categoryList} tokens={{ childrenGap: 12 }}>
          {actionItemRows.map((row) => (
            <Stack as="li" key={row.key} tokens={{ childrenGap: 4 }}>
              <Stack
                horizontal
                verticalAlign="center"
                horizontalAlign="space-between"
                tokens={{ childrenGap: 12 }}
              >
                <Text className={classes.categoryLabel}>{row.label}</Text>
                <StackItem disableShrink>
                  <Badge
                    appearance="ghost"
                    color={row.total > 0 && row.completed === row.total ? 'success' : 'informative'}
                    icon={
                      row.total > 0 && row.completed === row.total ? (
                        <CheckmarkCircleFilled aria-hidden="true" />
                      ) : undefined
                    }
                  >
                    {row.total > 0 ? `${row.completed}/${row.total}` : 'No tasks'}
                  </Badge>
                </StackItem>
              </Stack>
              {renderProgressBar(row)}
            </Stack>
          ))}
        </Stack>
      </div>
    );
  };

  return (
    <Card title="SAP Deactivation Progress" isLoading={loading && !progress} headingLevel={2}>
      {renderTaskProgress()}
    </Card>
  );
};

export default SAPDeactivationProgress;
